import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminShell from "../components/AdminShell";
import AppPage from "../components/AppPage";
import { adminUsersApi, adminResetUserLoginsApi } from "../api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("ru-RU");
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminUsers() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [noCompany, setNoCompany] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");
  const [resetting, setResetting] = useState(null);

  const load = async () => {
    setErr("");
    try {
      const data = await adminUsersApi({ q: search, no_company: noCompany, limit: 100 });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || t("admin.load_error"));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, noCompany]);

  const onResetLogins = async (userId, name) => {
    const label = name || userId;
    if (!window.confirm(t("admin.reset_logins_confirm", { name: label }))) return;
    setResetting(userId);
    setErr("");
    try {
      await adminResetUserLoginsApi(userId);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || t("admin.load_error"));
    } finally {
      setResetting(null);
    }
  };

  return (
    <AdminShell title={t("admin.nav.users")}>
      <AppPage
        bodyScroll="table"
        toolbar={(
          <>
      <form
        className="row g-2 mb-3 align-items-center"
        onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }}
      >
        <div className="col-md-5">
          <input
            className="form-control"
            placeholder={t("admin.search_user")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <button type="submit" className="btn btn-primary">{t("common.search")}</button>
        </div>
        <div className="col-auto">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="no-company-filter"
              checked={noCompany}
              onChange={(e) => setNoCompany(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="no-company-filter">
              {t("admin.filter_no_company")}
            </label>
          </div>
        </div>
        <div className="col-auto ms-md-auto small text-muted">
          {total} {t("admin.users_total")}
        </div>
      </form>

      {err && <div className="alert alert-danger">{err}</div>}
          </>
        )}
      >
      <table className="table table-hover mb-0 align-middle card border-0 shadow-sm">
            <thead className="table-light">
              <tr>
                <th>{t("common.fullname")}</th>
                <th>{t("common.email")}</th>
                <th>{t("common.company")}</th>
                <th>{t("common.role")}</th>
                <th>{t("admin.col.registered")}</th>
                <th>{t("admin.col.logins_total")}</th>
                <th>{t("admin.col.logins_web")}</th>
                <th>{t("admin.col.logins_app")}</th>
                <th>{t("admin.col.last_login")}</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-4">{t("admin.no_data")}</td>
                </tr>
              ) : rows.map((u) => (
                <tr key={u.user_id}>
                  <td>{u.name || "—"}</td>
                  <td className="small">{u.email_display || u.email || "—"}</td>
                  <td className="small">
                    {u.company_name ? (
                      <span className="text-success">{u.company_name}</span>
                    ) : (
                      <span className="text-warning">{t("admin.no_company_yet")}</span>
                    )}
                  </td>
                  <td className="small">{u.role || "—"}</td>
                  <td className="text-nowrap small">{fmtDate(u.created_at)}</td>
                  <td className="small fw-semibold">{u.login_count || 0}</td>
                  <td className="small">{u.login_count_web || 0}</td>
                  <td className="small">{u.login_count_app || 0}</td>
                  <td className="text-nowrap small">
                    {fmtDateTime(u.last_login_at)}
                    {u.last_login_client ? (
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        {u.last_login_client === "app" ? t("admin.client_app") : t("admin.client_web")}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={resetting === u.user_id || !(u.login_count || u.last_login_at)}
                      onClick={() => onResetLogins(u.user_id, u.name || u.email_display || u.email)}
                      title={t("admin.reset_logins")}
                      data-testid={`admin-reset-logins-${u.user_id}`}
                    >
                      {resetting === u.user_id ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        <i className="bi bi-arrow-counterclockwise"></i>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </AppPage>
    </AdminShell>
  );
}
